import { Typography, Box, Tooltip } from '@mui/material';
import { useStore } from '@store/GlobalStore';
import { DatePicker } from '@components/datePicker';
import { fakeLocalMidnightFromTZMidnight } from '@utils/date';
import { InfoCircle } from '@assets/svgs';
import { AvailableBrokerageOfferingsItem } from '@models/productOffering';
import { useNewQuoteStore } from '@store/NewQuote';
import { getEarliestEffectiveOn, getLatestEffectiveThrough } from '@utils/brokerages';
import { addDays } from 'date-fns';
import { IconButton } from '@components/iconButton';
import { shallow } from 'zustand/shallow';
import { useProductOfferings } from '@services/productOffering';
import { useProductOptions } from '@services/productOption';
import { uniqBy } from 'lodash';
import { QuoteSetupPresetGetResponse } from '@lio/api-types';
import { useGetScreenSize } from '@utils/useGetScreenSize';

type TPolicyDateSelection = {
  handleSelectedDateChange: (date: Date | null, context: { validationError: string | null }) => void;
  quotePreset?: QuoteSetupPresetGetResponse;
};

export const PolicyDateSelection = ({ handleSelectedDateChange, quotePreset }: TPolicyDateSelection) => {
  const { scope, isFormLoading } = useNewQuoteStore();
  const { brokerageAgency, productOffering, insuranceProductId, productOption, effectiveDate } = scope;
  const [userAuth] = useStore((state) => [state.userAuth], shallow);
  const { isInternalUser } = userAuth;
  const hasQuotePreset = quotePreset;
  const { productOfferings } = useProductOfferings({ brokerageId: brokerageAgency?.value });
  const { productOptions } = useProductOptions(insuranceProductId);
  const uniqueOfferings = productOfferings?.items.length
    ? uniqBy(productOfferings.items, (brokerageOffering) => brokerageOffering.id).map((p) => ({
        ...p,
        brokerageOfferingAvailability: {
          jurisdictions: p.brokerageOfferingAvailability.jurisdictions.map((j) => ({
            ...j,
            effectiveOn: new Date(j.effectiveOn),
            effectiveThrough: new Date(j.effectiveThrough),
          })),
        },
      }))
    : [];

  const datePickerParams = () => {
    const productId = productOffering?.value;
    const effectiveProduct = uniqueOfferings?.find((item: AvailableBrokerageOfferingsItem) => item.id === productId);
    const effectiveOn = effectiveProduct
      ? getEarliestEffectiveOn([...effectiveProduct.brokerageOfferingAvailability.jurisdictions])
      : new Date();
    const effectiveThroughData = effectiveProduct
      ? getLatestEffectiveThrough([...effectiveProduct.brokerageOfferingAvailability.jurisdictions])
      : null;
    const effectiveThrough = effectiveThroughData || addDays(new Date(), 120);
    return {
      startDate: isInternalUser ? effectiveOn : new Date(Math.max(new Date().valueOf(), effectiveOn.valueOf())),
      endDate: new Date(Math.min(addDays(new Date(), 120).valueOf(), effectiveThrough.valueOf())),
    };
  };
  const { isMobileScreen } = useGetScreenSize();
  const isDateRangeRestricted = () =>
    new Date(datePickerParams().endDate) < addDays(new Date(), 120) ||
    new Date(datePickerParams().startDate) > new Date();

  function isEffectiveDateDisabled(): boolean {
    if (isFormLoading) {
      return true;
    }
    if (hasQuotePreset) {
      return false;
    }
    if (productOffering) {
      return !!((uniqueOfferings.length && !productOffering) || (productOptions?.items.length && !productOption));
    }
    return true;
  }
  return (
    <>
      <Typography
        variant='h4'
        sx={{
          mt: '10px',
        }}
      >
        When should the policy be effective?
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <DatePicker
          fullWidth={isMobileScreen}
          value={effectiveDate !== null ? new Date(effectiveDate) : effectiveDate}
          data-testid='Effective-Date'
          disabled={isEffectiveDateDisabled()}
          onChange={handleSelectedDateChange}
          minDate={fakeLocalMidnightFromTZMidnight(datePickerParams().startDate)}
          maxDate={fakeLocalMidnightFromTZMidnight(datePickerParams().endDate)}
          inputLabel='Policy Effective Date'
        />

        {!isEffectiveDateDisabled() && isDateRangeRestricted() && (
          <Tooltip
            sx={{ marginRight: '-36px' }}
            placement='left-start'
            title='Date range is restricted due to product availability'
          >
            <IconButton variant='circularDark'>
              <InfoCircle />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </>
  );
};
